/**
 * A function to create a log object for the 'maActionClicked' event (evid: 156)
 *
 * Event description: uou clicked on action in members area
 *
 * @param {maActionClickedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} tabName - past_events/upcoming_events
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} action - see_other_events/expand_event_details/view_event_details/share_event/switch_between_tabs
 * @return {LogObject<maActionClickedParams>} LogObject<maActionClickedParams>
 */
export function maActionClicked(params) {
    return { evid: 156, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouAddToCalendarClick' event (evid: 182)
 *
 * Event description: UoU clicks on the 'Add to Calendar' button in the thank you page
 *
 * @param {uouAddToCalendarClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {string} orderId - order number (null for rsvp events)
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouAddToCalendarClickParams>} LogObject<uouAddToCalendarClickParams>
 */
export function uouAddToCalendarClick(params) {
    return { evid: 182, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouSchedulePageLoaded' event (evid: 191)
 *
 * Event description: triggered when UoU opens the schedule page
 *
 * @param {uouSchedulePageLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {string} origin - {'event_details_page', 'direct'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {number} schedule_item_count - the amount of items in the schedule
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouSchedulePageLoadedParams>} LogObject<uouSchedulePageLoadedParams>
 */
export function uouSchedulePageLoaded(params) {
    return { evid: 191, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouSchedulePlaceFilterApplied' event (evid: 192)
 *
 * Event description: triggered when UoU applies a place filter on the schedule items
 *
 * @param {uouSchedulePlaceFilterAppliedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {boolean} is_place_filtered - whether place is filtered
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} is_tags_filtered - whether tags are filtered
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouSchedulePlaceFilterAppliedParams>} LogObject<uouSchedulePlaceFilterAppliedParams>
 */
export function uouSchedulePlaceFilterApplied(params) {
    return { evid: 192, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouScheduleItemDescriptionView' event (evid: 194)
 *
 * Event description: triggered when UoU clicks on 'read more' on one of the schedule items
 *
 * @param {uouScheduleItemDescriptionViewParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} schedule_item_id - schedule item guid
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouScheduleItemDescriptionViewParams>} LogObject<uouScheduleItemDescriptionViewParams>
 */
export function uouScheduleItemDescriptionView(params) {
    return { evid: 194, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'userLiveVideoShareScreenToggle' event (evid: 216)
 *
 * Event description: UoU bookmarks one of the items available
 *
 * @param {userLiveVideoShareScreenToggleParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} schedule_item_id - scheduled item's id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {string} origin - {'schedule','item details','my agenda'}
 * @property {string} status - {'on','off'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_id
 * @return {LogObject<userLiveVideoShareScreenToggleParams>} LogObject<userLiveVideoShareScreenToggleParams>
 */
export function userLiveVideoShareScreenToggle(params) {
    return { evid: 216, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'userLiveVideoChangeLayoutClick' event (evid: 217)
 *
 * Event description: UoU clicks on the 'view my agenda' button for schedule items to view 'my agenda' modal.
 *
 * @param {userLiveVideoChangeLayoutClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} schedule_item_id - schedule item id, if clicked from item details. Else NULL
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {string} origin - {'schedule','item details'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event id
 * @return {LogObject<userLiveVideoChangeLayoutClickParams>} LogObject<userLiveVideoChangeLayoutClickParams>
 */
export function userLiveVideoChangeLayoutClick(params) {
    return { evid: 217, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'userLiveVideoChatOpened' event (evid: 218)
 *
 * Event description: UoU clicks on one of the buttons available for login/logout. Will not generate an entry if the area belongs to Members Area
 *
 * @param {userLiveVideoChatOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} button_name - {'login','logout'}
 * @property {number} event_rank - event_rank
 * @property {string} origin - {'schedule','item details'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_id if applicable
 * @return {LogObject<userLiveVideoChatOpenedParams>} LogObject<userLiveVideoChatOpenedParams>
 */
export function userLiveVideoChatOpened(params) {
    return { evid: 218, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'userLiveVideoStartVideoClick' event (evid: 220)
 *
 * Event description: Ticket added to apple wallet
 *
 * @param {userLiveVideoStartVideoClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_guid
 * @return {LogObject<userLiveVideoStartVideoClickParams>} LogObject<userLiveVideoStartVideoClickParams>
 */
export function userLiveVideoStartVideoClick(params) {
    return { evid: 220, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'userLiveVideoEndVideoClick' event (evid: 229)
 *
 * Event description: UoU clicks 'Join' in the Groups section of the thank you page after confirming rsvp/buying a ticket.
 *
 * @param {userLiveVideoEndVideoClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} group_id - group_id
 * @property {GUID} biToken - msid
 * @property {GUID} orderId - order number; null if RSVP
 * @property {GUID} visitor_id - visitor_id indicating unique UoU
 * @property {number} event_rank - event_rank
 * @property {string} origin - {'thankyou_page'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_id
 * @return {LogObject<userLiveVideoEndVideoClickParams>} LogObject<userLiveVideoEndVideoClickParams>
 */
export function userLiveVideoEndVideoClick(params) {
    return { evid: 229, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'downloadTicketIntent' event (evid: 239)
 *
 * Event description: UoU shows interest in downloading ticket.
 * For mobile web, clicking 'Download Ticket' button from thank you page.
 *
 * @param {downloadTicketIntentParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor id
 * @property {number} event_rank - event_rank
 * @property {string} origin - {'thank you page'}
 * @property {boolean} show_image - show_image
 * @property {boolean} is_mobile - True if UoU is making the action from mobile web, else False.
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event id
 * @return {LogObject<downloadTicketIntentParams>} LogObject<downloadTicketIntentParams>
 */
export function downloadTicketIntent(params) {
    return { evid: 239, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouSeatingMapTicketSelectionClick' event (evid: 251)
 *
 * Event description: User selects or removes an available seat from the map or ticket list
 *
 * @param {uouSeatingMapTicketSelectionClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} seat_guid - unique seat id, null if whole table reservation is required
 * @property {GUID} biToken - msid
 * @property {GUID} seating_map_guid - seating_map_guid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} event_guid - event id
 * @property {number} event_rank - event_rank
 * @property {string} element_guid - element id
 * @property {string} origin - {'map','ticket_list','basket'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {boolean} is_whole_table - true if the selected element required whole table reservation
 * @property {string} element_type - element type
 * @property {string} action - {'select','delete'}
 * @return {LogObject<uouSeatingMapTicketSelectionClickParams>} LogObject<uouSeatingMapTicketSelectionClickParams>
 */
export function uouSeatingMapTicketSelectionClick(params) {
    return { evid: 251, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouSeatingMapTabOpened' event (evid: 252)
 *
 * Event description: UoU opens one of the tabs available in the ticket selection
 *
 * @param {uouSeatingMapTabOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {string} tab_name - {'basket','ticket_list'}
 * @property {GUID} seating_map_guid - seating_map_guid
 * @property {GUID} visitor_id - visitor_id
 * @property {GUID} event_guid - event id
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @return {LogObject<uouSeatingMapTabOpenedParams>} LogObject<uouSeatingMapTabOpenedParams>
 */
export function uouSeatingMapTabOpened(params) {
    return { evid: 252, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouSeatingMapTicketListFilterApplied' event (evid: 253)
 *
 * Event description: uou_seating_map_ticket_list_filter_applied.
 *
 * @param {uouSeatingMapTicketListFilterAppliedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} filterValue - value selected for the filter
 * @property {GUID} biToken - msid
 * @property {GUID} seating_map_guid - seating id
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {string} filter_name - {'price','zone','refresh_filters'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event id
 * @return {LogObject<uouSeatingMapTicketListFilterAppliedParams>} LogObject<uouSeatingMapTicketListFilterAppliedParams>
 */
export function uouSeatingMapTicketListFilterApplied(params) {
    return { evid: 253, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouSeatingMapButtonClick' event (evid: 254)
 *
 * Event description: UoU clicks one of the available buttons in the map section of the ticket selector
 *
 * @param {uouSeatingMapButtonClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} seating_map_guid - seating id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} button_name - {'show_prices','hide_prices','zoom_in','zoom_out'}
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event_guid
 * @return {LogObject<uouSeatingMapButtonClickParams>} LogObject<uouSeatingMapButtonClickParams>
 */
export function uouSeatingMapButtonClick(params) {
    return { evid: 254, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouOpenRegFormPopupClick' event (evid: 503)
 *
 * Event description: click on opening reg form by UoU
 *
 * @param {uouOpenRegFormPopupClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {number} questions_count - questions_count (send null at this stage)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} destination_type - pop_up form (the old one), page_form (hiding page, no ticket), page_details (with or w/out ticket)
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp_id
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouOpenRegFormPopupClickParams>} LogObject<uouOpenRegFormPopupClickParams>
 */
export function uouOpenRegFormPopupClick(params) {
    return { evid: 503, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouPageDetailsLoaded' event (evid: 507)
 *
 * Event description: opening page details loaded by UoU
 *
 * @param {uouPageDetailsLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} ticket_definition_count - null if no tickets allowed/shown
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} type_of_event - {'rsvp','ticketed'}
 * @property {number} event_rank - event_rank
 * @property {string} origin - members area/ event list
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} currency - currency
 * @property {string} comp_id - comp_id
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouPageDetailsLoadedParams>} LogObject<uouPageDetailsLoadedParams>
 */
export function uouPageDetailsLoaded(params) {
    return { evid: 507, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouPageDetailsRegisterClick' event (evid: 508)
 *
 * Event description: clicking on checkout (for ticketed events) or register now for the rest
 * by UoU
 *
 * @param {uouPageDetailsRegisterClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} ticket_definition_count - null if no tickets allowed (unique ticket_def_id)
 * @property {number} ticket_count - null if no tickets allowed (absolute number of item purchased)
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} type_of_event - {'rsvp','ticketed'}
 * @property {number} event_rank - event_rank
 * @property {number} nonfree_ticket_count - same as ticket_count, but for non-free tickets only
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} currency - currency (or null)
 * @property {string} comp_id - comp_id
 * @property {number} total_order_revenue - money amount (null if no ticket, 0 if free ticket was purchased)
 * @property {GUID} event_id - event_id
 * @property {boolean} is_member_only_event - t/f
 * @return {LogObject<uouPageDetailsRegisterClickParams>} LogObject<uouPageDetailsRegisterClickParams>
 */
export function uouPageDetailsRegisterClick(params) {
    return { evid: 508, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouFormPageLoaded' event (evid: 509)
 *
 * Event description: form page loaded by UoU
 *
 * @param {uouFormPageLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} ticket_definition_count - null if no tickets allowed (unique ticket_def_id)
 * @property {number} ticket_count - null if no tickets allowed (absolute number of item purchased)
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {number} nonfree_ticket_count - same as ticket_count, but for non-free tickets only
 * @property {string} origin - null if didn't came from details page, 'details page' otherwise
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} currency - currency (or null)
 * @property {string} comp_id - comp_id
 * @property {number} total_order_revenue - money amount (null if no ticket, 0 if free ticket was purchased)
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouFormPageLoadedParams>} LogObject<uouFormPageLoadedParams>
 */
export function uouFormPageLoaded(params) {
    return { evid: 509, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouFormPageSubmitClick' event (evid: 510)
 *
 * Event description: form page submit click by UoU:
 * button_name = 'submit' when no paid tickets are involved
 * button_name = 'chose_payment' when to cashier component
 *
 * @param {uouFormPageSubmitClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} ticket_definition_count - null if no tickets allowed (unique ticket_def_id)
 * @property {number} ticket_count - null if no tickets allowed (absolute number of item purchased)
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} button_name - submit, choose payment
 * @property {number} event_rank - event_rank
 * @property {number} nonfree_ticket_count - same as ticket_count, but for non-free tickets only
 * @property {number} visitor_rsvp_count - (null if ticketed) 1 is the visitor have responded for himself, 2 or more if the user choose "plus one" option
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} currency - currency (or null)
 * @property {string} comp_id - comp_id
 * @property {number} total_order_revenue - money amount (null if no ticket, 0 if free ticket was purchased)
 * @property {GUID} event_id - event_id
 * @property {string} rsvp_type - (null if ticketed) {'yes', 'no', 'maybe' etc)
 * @return {LogObject<uouFormPageSubmitClickParams>} LogObject<uouFormPageSubmitClickParams>
 */
export function uouFormPageSubmitClick(params) {
    return { evid: 510, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouTicketedThankyouPageLoaded' event (evid: 511)
 *
 * Event description: Thanks you page (ticketed version only) loaded for UoU with 4 state:
 * error, cancelled, pending, success
 *
 * @param {uouTicketedThankyouPageLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {number} ticket_definition_count - null if no tickets allowed (unique ticket_def_id)
 * @property {number} ticket_count - null if no tickets allowed (absolute number of item purchased)
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} paymentmethod - paymentmethod
 * @property {number} event_rank - event_rank
 * @property {number} nonfree_ticket_count - same as ticket_count, but for non-free tickets only
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} currency - currency (or null)
 * @property {string} comp_id - comp_id
 * @property {number} total_order_revenue - money amount (null if no ticket, 0 if free ticket was purchased)
 * @property {GUID} event_id - event_id
 * @property {string} page_type - error, cancelled, pending, success
 * @return {LogObject<uouTicketedThankyouPageLoadedParams>} LogObject<uouTicketedThankyouPageLoadedParams>
 */
export function uouTicketedThankyouPageLoaded(params) {
    return { evid: 511, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouNonticketedThankyouPageLoaded' event (evid: 512)
 *
 * Event description: Thanks you page (non-ticketed version only) loaded for UoU
 *
 * @param {uouNonticketedThankyouPageLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {number} visitor_rsvp_count - 1 is the visitor have respinded for himself, 2 or more if the user choose "plus one" option
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp_id
 * @property {GUID} event_id - event_id
 * @property {string} rsvp_type - {'yes', 'no', 'maybe' etc)
 * @return {LogObject<uouNonticketedThankyouPageLoadedParams>} LogObject<uouNonticketedThankyouPageLoadedParams>
 */
export function uouNonticketedThankyouPageLoaded(params) {
    return { evid: 512, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouShareEventClick' event (evid: 513)
 *
 * Event description: uou_share_event_click
 *
 * @param {uouShareEventClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} button_name - {'facebook','twitter','linkedin', etc}
 * @property {number} event_rank - event_rank
 * @property {string} origin - widget, page details, (from thank you page -->) success, error etc
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp_id
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouShareEventClickParams>} LogObject<uouShareEventClickParams>
 */
export function uouShareEventClick(params) {
    return { evid: 513, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouClickedOnEnterCouponCode' event (evid: 514)
 *
 * Event description: uou clicked on 'enter coupon code'
 *
 * @param {uouClickedOnEnterCouponCodeParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp_id
 * @property {GUID} event_id - event_id
 * @return {LogObject<uouClickedOnEnterCouponCodeParams>} LogObject<uouClickedOnEnterCouponCodeParams>
 */
export function uouClickedOnEnterCouponCode(params) {
    return { evid: 514, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouCouponCodeApplied' event (evid: 515)
 *
 * Event description: uou coupon code applied
 *
 * @param {uouCouponCodeAppliedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp_id
 * @property {string} ticket_types - array of ticket ids from order
 * @property {GUID} event_id - event_id
 * @property {GUID} couponId - coupon_id
 * @return {LogObject<uouCouponCodeAppliedParams>} LogObject<uouCouponCodeAppliedParams>
 */
export function uouCouponCodeApplied(params) {
    return { evid: 515, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'seatingsPricingOptionModalOpened' event (evid: 516)
 *
 * Event description: Modal for seatings pricing options selection.
 *
 * @param {seatingsPricingOptionModalOpenedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} seat_guid - unique seat id, null if whole table reservation is required
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} pricing_options - json value for all pricing options
 * @property {number} event_rank - event_rank
 * @property {string} element_guid - element id
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {boolean} is_whole_table - true if the selected element required whole table reservation
 * @property {string} element_type - element type
 * @property {GUID} event_id - event_guid
 * @property {GUID} msid - msid
 * @property {GUID} ticket_definition_id - ticket_definition_id
 * @return {LogObject<seatingsPricingOptionModalOpenedParams>} LogObject<seatingsPricingOptionModalOpenedParams>
 */
export function seatingsPricingOptionModalOpened(params) {
    return { evid: 516, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'seatingsPricingOptionModalSelected' event (evid: 517)
 *
 * Event description: UoU modal selection - Cancel (or X button), Add to basket
 *
 * @param {seatingsPricingOptionModalSelectedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} seat_guid - unique seat id, null if whole table reservation is required
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} biToken - msid
 * @property {string} selected_pricing_options - json value for selected pricing options
 * @property {GUID} visitor_id - visitor_id
 * @property {string} pricing_options - json value for all pricing options
 * @property {number} event_rank - event_rank
 * @property {string} element_guid - element id
 * @property {string} selection - {'Close','Add to basket'}
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {boolean} is_whole_table - true if the selected element required whole table reservation
 * @property {string} element_type - element type
 * @property {GUID} event_id - event_guid
 * @property {GUID} msid - msid
 * @property {GUID} ticket_definition_id - ticket_definition_id
 * @return {LogObject<seatingsPricingOptionModalSelectedParams>} LogObject<seatingsPricingOptionModalSelectedParams>
 */
export function seatingsPricingOptionModalSelected(params) {
    return { evid: 517, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouTicketsMorePricesClick' event (evid: 518)
 *
 * Event description: UoU clicks on the More Prices + accordion available in the View Tickets window when a ticket has more than one price option available
 *
 * @param {uouTicketsMorePricesClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance id
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {GUID} event_id - event guid
 * @property {GUID} ticket_definition_id - ticket id
 * @return {LogObject<uouTicketsMorePricesClickParams>} LogObject<uouTicketsMorePricesClickParams>
 */
export function uouTicketsMorePricesClick(params) {
    return { evid: 518, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'wixEventsWidgetLoaded' event (evid: 80)
 *
 * Event description: user/uou saw fully loaded events widget
 *
 * @param {wixEventsWidgetLoadedParams} params - The parameters for this event
 * @property {GUID} instance_id - instance_id
 * @property {string} display_type - multiple/single
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor_id
 * @property {boolean} narrow_layout - narrow_layout
 * @property {number} event_rank - event_rank
 * @property {boolean} has_image - t/f
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {boolean} is_main_page - true/false
 * @property {string} layout - grid/list/side by side/ calendar
 * @return {LogObject<wixEventsWidgetLoadedParams>} LogObject<wixEventsWidgetLoadedParams>
 */
export function wixEventsWidgetLoaded(params) {
    return { evid: 80, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouCancelRsvpClick' event (evid: 87)
 *
 * Event description: clicked on cancel rsvp
 *
 * @param {uouCancelRsvpClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor
 * @property {number} event_rank - event_rank
 * @property {string} origin - members area/thank you page
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp
 * @property {GUID} event_id - event id
 * @return {LogObject<uouCancelRsvpClickParams>} LogObject<uouCancelRsvpClickParams>
 */
export function uouCancelRsvpClick(params) {
    return { evid: 87, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouDownloadTickets' event (evid: 88)
 *
 * Event description: clicked on download tickets
 *
 * @param {uouDownloadTicketsParams} params - The parameters for this event
 * @property {GUID} instance_id - instance
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor
 * @property {string} button_name - {'download_pdf', 'add_to_apple_wallet'}
 * @property {number} event_rank - event_rank
 * @property {string} origin - {'members area', 'thank you page'}
 * @property {boolean} show_image - show_image
 * @property {boolean} is_mobile - True if UoU is making the action from mobile web, else False. From 2021-03-29
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp
 * @property {GUID} event_id - event
 * @return {LogObject<uouDownloadTicketsParams>} LogObject<uouDownloadTicketsParams>
 */
export function uouDownloadTickets(params) {
    return { evid: 88, src: 68, endpoint: 'events-uou', params: params };
}
/**
 * A function to create a log object for the 'uouSeeAllGuestsClick' event (evid: 89)
 *
 * Event description: clicked on see all guests from event details
 *
 * @param {uouSeeAllGuestsClickParams} params - The parameters for this event
 * @property {GUID} instance_id - instance
 * @property {GUID} biToken - msid
 * @property {GUID} visitor_id - visitor
 * @property {number} event_rank - event_rank
 * @property {boolean} show_image - show_image
 * @property {number} total_events_count - total_events_count
 * @property {string} comp_id - comp
 * @property {GUID} event_id - event
 * @return {LogObject<uouSeeAllGuestsClickParams>} LogObject<uouSeeAllGuestsClickParams>
 */
export function uouSeeAllGuestsClick(params) {
    return { evid: 89, src: 68, endpoint: 'events-uou', params: params };
}
//# sourceMappingURL=index.js.map